import { Component, NgZone, OnDestroy } from '@angular/core';
import { BaseComponent } from '../../shared/base/base.component';
import * as L from 'leaflet';
import { BaseService } from '../../../providers/base.service';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import 'leaflet-control-geocoder';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IssueFormComponent } from '../../home/issue-form/issue-form.component';
import { LocateControl } from "leaflet.locatecontrol";
import { addCenterControl } from '../map-center-control';

@Component({
  selector: 'app-fibermap',
  templateUrl: './fibermap.component.html',
  styleUrls: ['./fibermap.component.scss']
})
export class FibermapComponent extends BaseComponent implements OnDestroy {
  layers: any = [];
  map: L.Map;
  marker: L.Marker;
  searchQuery: string = '';
  searchResults: any[] = [];
  isPublic: boolean; // the route outside the layout, so the map takes the whole window

  // Without a login (the public fiber-map page) the map says only whether the
  // point is covered: no FAT polygons, no FAT names. A guest's request is always
  // an inquiry, which the API files as `system` and matches to a FAT itself.
  get guest(): boolean {
    return !this.token;
  }

  constructor(
    public service: BaseService,
    public translator: TranslateService,
    public cookieService: CookieService,
    public titleService: Title,
    private zone: NgZone,
    public activeModal: NgbModal,
    public route: ActivatedRoute,) {
    super(service, translator, cookieService, titleService);
    this.isPublic = !!this.route.snapshot.data.public;
  }

  ngOnDestroy(): void {
    this.map = null;
    this.marker = null;
  }

  async ngOnInit() {
    await super.ngOnInit();
    if (this.isPublic) {
      this.translator.get('FIBER_MAP').subscribe(title => this.pageTitle = title);
    }
    // this.addLongClickListener();
  }

  mapOptions = {
    layers: [L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; Petiak System'
    })],
    zoom: 18,
    center: L.latLng([this.latitude || 35.77880664304775, this.longitude || 51.43510222434998])
  };

  async onMapReady(map: L.Map) {
    map.invalidateSize();
    this.map = map;
    // On the public page nothing has loaded the translations yet, and the
    // controls and the popup below take their texts with instant()
    await this.translator.get('SEARCH').toPromise();
    if (!this.map) {
      return; // destroyed meanwhile
    }

    new LocateControl({
      position: 'bottomleft',
      keepCurrentZoomLevel: true,
      strings: {
        title: this.translator.instant('MAP_LOCATOR_TITLE')
      }
    }).addTo(this.map);

    // Define the custom search control
    const SearchControl = L.Control.extend({
      options: {
        position: 'topright' // Position of the control
      },
      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom samim');
        container.style.backgroundColor = 'white';
        container.style.padding = '5px';
        container.style.borderRadius = '5px';

        // Create the input box
        const input = L.DomUtil.create('input', '', container);
        input.type = 'text';
        input.placeholder = this.translator.instant('SEARCH');
        input.style.width = '200px';
        input.style.fontSize = 'larger';
        input.style.border = 'none';
        input.style.outline = 'none';
        // input.style.padding = '5px';

        // Create the results container
        const resultsContainer = L.DomUtil.create('ul', 'search-results', container);
        resultsContainer.style.listStyle = 'none';
        resultsContainer.style.margin = '0';
        resultsContainer.style.padding = '0';
        resultsContainer.style.maxHeight = '300px';
        resultsContainer.style.overflowY = 'auto';
        resultsContainer.style.backgroundColor = 'white';
        resultsContainer.style.border = '1px solid #ccc';
        resultsContainer.style.display = 'none'; // Initially hidden

        // Disable map scroll zoom when interacting with the results container
        L.DomEvent.on(resultsContainer, 'mouseover', () => {
          this.map.scrollWheelZoom.disable();
        });

        // Re-enable map scroll zoom when leaving the results container
        L.DomEvent.on(resultsContainer, 'mouseout', () => {
          this.map.scrollWheelZoom.enable();
        });

        // Handle input events
        L.DomEvent.on(input, 'input', async () => {
          this.searchQuery = input.value;
          if (this.searchQuery.trim().length > 2) {
            await this.onSearch();
            resultsContainer.innerHTML = ''; // Clear previous results
            if (this.searchResults.length > 0) {
              resultsContainer.style.display = 'block'; // Show results container
              this.searchResults.forEach(result => {
                const resultItem = L.DomUtil.create('li', '', resultsContainer);
                resultItem.textContent = result.name;
                resultItem.style.fontSize = 'larger';
                resultItem.style.padding = '5px';
                resultItem.style.cursor = 'pointer';
                resultItem.style.borderBottom = '1px solid #eee';

                // Highlight on hover
                resultItem.addEventListener('mouseover', () => {
                  resultItem.style.backgroundColor = '#f0f0f0';
                });
                resultItem.addEventListener('mouseout', () => {
                  resultItem.style.backgroundColor = 'white';
                });

                // Handle result click
                L.DomEvent.on(resultItem, 'click', () => {
                  this.onResultClick(result);
                  resultsContainer.style.display = 'none'; // Hide results after selection
                });
              });
            } else {
              resultsContainer.style.display = 'none'; // Hide if no results
            }
          } else {
            resultsContainer.style.display = 'none'; // Hide if query is too short
          }
        });

        // Prevent map interactions when interacting with the input or results
        L.DomEvent.disableClickPropagation(container);

        return container;
      }
    });

    // Add the custom search control to the map
    const searchControl = new SearchControl();
    map.addControl(searchControl);

    // Latitude and longitude boxes, to centre the map on a typed point
    addCenterControl(map, this.translator);

    // Create a marker and add it to the map's center
    this.marker = L.marker(this.map.getCenter(), {
      draggable: false,
      icon: L.icon({
        iconUrl: `assets/leaflet/marker-icon.png`,
        iconRetinaUrl: `assets/leaflet/marker-icon-2x.png`,
        shadowUrl: `assets/leaflet/marker-shadow.png`,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [0, -40]
      })
    }).addTo(this.map);

    // Adding a click listener to the popup link
    this.marker.on('popupopen', () => {
      const link = document.getElementById('popup-link');
      if (link) {
        link.addEventListener('click', (event: MouseEvent) => {
          event.preventDefault(); // Prevent default link behavior
          const data = {
            fat_id: (event.target as HTMLElement).getAttribute('data-id'),
            fat_name: (event.target as HTMLElement).getAttribute('data-name'),
            fat_shaskam_id: (event.target as HTMLElement).getAttribute('data-shaskam-id'),
            latitude: this.marker.getLatLng().lat,
            longitude: this.marker.getLatLng().lng,
          }
          // Call the method from Angular
          this.zone.run(() => {
            this.onPopupLinkClick(data);
          });
        });
      }
    });

    await this.check(this.map.getCenter());

    // Listen for 'move' or 'moveend' events and update the marker position
    this.map.on('move', () => {
      this.marker.closePopup();
      const center = this.map.getCenter();
      this.marker.setLatLng(center); // Update marker position to the map's center
    });

    this.map.on('moveend', async () => {
      const center = this.map.getCenter();
      await this.check(center);
    });

    const features = this.guest ? null : await this.service.apiCall('/api/fiber_features', {}).toPromise();
    if (features && features.length) {
      features.forEach(polygon => {
        (L as any).geoJSON(polygon, { color: 'red' }).addTo(map).bindPopup(`<b>${polygon.properties.name}</b>`);
      });
    }
  }

  lastCheck = {};
  private async check(center: any) {
    if (JSON.stringify(center) != JSON.stringify(this.lastCheck)) {
      const check = await this.service.apiCall('/api/fiber_check', center).toPromise();
      this.lastCheck = center;
      if (this.guest) {
        const verdict = document.createElement('div');
        verdict.className = 'samim';
        verdict.textContent = this.translator.instant(check.inside ? 'FIBER_COVERED' : 'FIBER_NOT_COVERED');
        this.marker.bindPopup(`${verdict.outerHTML}<a class="samim" href="#" id="popup-link">${this.translator.instant('ISSUE_CREATE')}</a>`).openPopup();
      } else if (check.inside) {
        this.marker.bindPopup(`<a class="samim" href="#" id="popup-link" data-id="${check.polygon.properties.fat_id}" data-shaskam-id="${check.polygon.properties.fat_shaskam_id}" data-name="${check.polygon.properties.name}">${this.translator.instant('ISSUE_CREATE_LEAD')}</a>`).openPopup();
      } else {
        this.marker.bindPopup(`<a class="samim" href="#" id="popup-link"">${this.translator.instant('ISSUE_CREATE_INQUIRY')}</a>`).openPopup();
      }
    }
  }

  async onSearch() {
    if (this.searchQuery.trim().length > 3) {
      try {
        const center = this.map.getCenter();
        const result = await this.service.apiCall('/api/map_search', { term: this.searchQuery.trim(), center }).toPromise();
        if (result.count) {
          this.searchResults = result.items.map(r => ({ name: `${r.title} (${r.neighbourhood || ''} ${r.region})`, latitude: r.location.y, longitude: r.location.x }))
        } else {
          this.searchResults = []
        }

      } catch (error) {
        console.error('Error fetching search results:', error);
      }
    } else {
      this.searchResults = [];
    }
  }

  onResultClick(result: any) {
    this.searchResults = []; // Clear the search results
    const latLng = L.latLng(result.latitude, result.longitude);
    this.map.setView(latLng, 18); // Pan the map to the selected location
    this.marker.setLatLng(latLng); // Move the marker to the selected location
    // this.marker.bindPopup(`<b>${result.name}</b>`).openPopup();
  }

  onPopupLinkClick(data) {
    if (this.guest) {
      data = { latitude: +data.latitude.toFixed(6), longitude: +data.longitude.toFixed(6) };
    }
    const issueForm = this.activeModal.open(IssueFormComponent, { size: 'lg' });
    issueForm.componentInstance.params = {
      department: 'sales',
      type: data.fat_shaskam_id ? 'lead-fiber' : 'inquiry-fiber',
      data
    };
  }

}
